import { When } from 'react-if'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { Seo } from '@/layout/Seo'
import { MovieJsonLd } from '@/layout/Seo/JsonLd'
import { NextPageWithLayout } from '@/layouts/types'
import { TheatricalFooter, TheatricalFooterProps } from '@/organisms/TheatricalFooter'
import {
  CatalogMovie,
  CatalogSeries,
  fetchTitle,
  formatTitleReleaseYear,
  mapJsonLdProps,
  mapSeoProps,
} from '@/services/ContentCatalog'
import { DigitalPageData, getDigitalPageBySlug } from '@/services/DigitalService'
import { getProjectLivestreams, PlayableLivestream } from '@/services/LivestreamService'
import { isStreaming } from '@/services/ProjectPhaseService'
import { getProjectAbout, getWatchableContentByGuid, ProjectMeta } from '@/services/ProjectsService'
import {
  formatFilmName,
  getCurrentYear,
  getTheatricalPreReleasePageData,
  GetTheatricalPreReleasePageDataResult,
  useDigitalTitle,
} from '@/services/TheatricalPreReleaseService'
import { getWhereToWatch, WhereToWatch } from '@/services/WhereToWatchService'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { noPreRenderedStaticPaths } from '@/utils/nextUtils/staticPages'
import { omitUndefineds } from '@/utils/object'
import { loadTranslations } from '@/utils/translate/translate-server'
import { DigitalStreamingView } from '@/views/WatchTheatricalView'
import { WatchTheatricalContextProvider } from '@/views/WatchTheatricalView/WatchTheatricalContext'

interface PageProps extends ProjectMeta {
  catalogTitle: CatalogMovie | CatalogSeries | null
  isStreaming: boolean
  livestreams: PlayableLivestream[]
  locale: string
  pageData: GetTheatricalPreReleasePageDataResult
  whereToWatch: WhereToWatch
  digitalPageData: DigitalPageData
  projectMeta: ProjectMeta
}

export const DigitalPage: NextPageWithLayout<PageProps> = (props) => {
  const { locale, pageData, slug, catalogTitle, livestreams, whereToWatch, digitalPageData, projectMeta } = props
  const { filmDetails } = pageData

  const path = `${paths.movies.index}/${slug}`
  const title = useDigitalTitle({
    filmName: formatFilmName(catalogTitle),
    releaseYear: formatTitleReleaseYear(catalogTitle) || getCurrentYear(),
  })

  return (
    <>
      <Seo {...mapSeoProps({ path, title, catalogTitle, locale, projectMeta })} path={path} />
      {catalogTitle && <MovieJsonLd {...mapJsonLdProps({ path, catalogTitle, locale })} />}
      <ProjectMetaProvider project={props}>
        <WatchTheatricalContextProvider pageData={pageData}>
          <DigitalStreamingView
            catalogTitle={catalogTitle}
            livestreams={livestreams}
            whereToWatch={whereToWatch}
            digitalPageData={digitalPageData}
          />
          <When condition={props.slug === slugs.soundOfFreedom || props.slug === slugs.theShift}>
            <TheatricalFooter {...(filmDetails?.filmAssociationLogos as TheatricalFooterProps)} slug={props.slug} />
          </When>
        </WatchTheatricalContextProvider>
      </ProjectMetaProvider>
    </>
  )
}

export const getStaticPaths = noPreRenderedStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<PageProps>(
  { filePath: 'pages/[locale]/digital/[slug]/index.tsx' },
  async ({ federationClient, locale, next404, params, preview }) => {
    const projectSlug = getItemOrFirstEntry(params.slug)
    if (!projectSlug) return next404()

    const [projectMetaRaw, digitalPageData, pageData] = await Promise.all([
      getProjectAbout({ slug: projectSlug }, federationClient, { locale, preview }),
      getDigitalPageBySlug({ slug: projectSlug, opts: { preview, locale } }),
      getTheatricalPreReleasePageData({ slug: projectSlug }, federationClient, { locale, preview }),
    ])

    if (!projectMetaRaw) return next404()
    if (!digitalPageData) return next404(Durations.FIVE_MINUTES_IN_SECONDS)
    if (!pageData) return next404(Durations.FIVE_MINUTES_IN_SECONDS)

    const [livestreams, catalogTitle, whereToWatch, watchableContent, translations] = await Promise.all([
      getProjectLivestreams(projectMetaRaw, projectSlug, federationClient),
      fetchTitle<CatalogMovie>(locale, pageData.contentCatalogId),
      getWhereToWatch(projectSlug, preview),
      getWatchableContentByGuid(projectMetaRaw?.title?.id, federationClient),
      loadTranslations(locale, [
        'common',
        'home',
        'watch',
        'app-promo',
        'ads',
        'account',
        'theatrical-presales',
        'pif',
      ]),
    ])

    let projectMeta = projectMetaRaw
    if (projectMeta.slug === undefined) {
      projectMeta = { ...projectMeta, slug: projectSlug }
    }
    if (projectMeta.public === undefined || (!projectMeta.public && catalogTitle)) {
      projectMeta = { ...projectMeta, public: true }
    }

    return {
      props: {
        catalogTitle: omitUndefineds(catalogTitle),
        isStreaming: isStreaming(watchableContent),
        livestreams,
        locale,
        pageData,
        digitalPageData,
        whereToWatch,
        projectMeta,
        ...projectMeta,
        ...translations,
      },
      revalidate: catalogTitle ? Durations.FIFTEEN_MINUTES_IN_SECONDS : Durations.ONE_MINUTE_IN_SECONDS,
    }
  },
)

export default DigitalPage
